import { createFileRoute } from '@tanstack/react-router';

import { NotFoundState } from '@/shared/components/NotFoundState';
import { LinkedRunPage, runSearchSchema } from '@/features/linked-runs';

export const Route = createFileRoute(
  '/workspaces/$workspaceId/workflows/$workflowKey/tasks/$taskId/run',
)({
  // The drawer's own open task/tab, the same `?task=`/`?tab=` mechanism
  // `board.tsx` uses (research.md Decision 5) — nothing else this page's
  // address needs to carry.
  validateSearch: runSearchSchema,
  // Deliberately no prefetching loader, for the same reason `board.tsx` has
  // none: the page renders its own pending state from `useWorkspace`, and a
  // loader that refetched here would risk discarding whatever the cache
  // already holds.
  component: RunRoute,
  notFoundComponent: RunNotFound,
});

export function RunRoute() {
  const { workspaceId, workflowKey, taskId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <LinkedRunPage
      workspaceId={workspaceId}
      workflowKey={workflowKey}
      taskId={taskId}
      openTaskId={search.task}
      openTab={search.tab}
      previewPath={search.preview}
      executionId={search.execution}
      view={search.view}
      onViewChange={(view) => {
        // Written either way: absent means "picked by size", so a choice of
        // `board` has to be said to stick.
        void navigate({
          search: (prev) => ({ ...prev, view }),
          replace: true,
        });
      }}
      onOpenTask={(openedTaskId) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            task: openedTaskId,
            // An open preview belongs to the task it was opened from (spec
            // 140 FR-064), the same rule the board's open-task write keeps:
            // only a re-open of the very task the panel was opened from keeps
            // it, and `onCloseTask` below already strips it for the same
            // reason.
            preview: prev.task === openedTaskId ? prev.preview : undefined,
          }),
        });
      }}
      onTabChange={(tab, options) => {
        void navigate({
          search: (prev) => ({ ...prev, tab }),
          replace: options?.replace ?? false,
        });
      }}
      onCloseTask={() => {
        void navigate({
          search: (prev) => ({
            ...prev,
            task: undefined,
            tab: undefined,
            preview: undefined,
          }),
        });
      }}
      onPreviewClose={() => {
        // Only the preview leaves: the drawer and its tab stay as they are.
        void navigate({
          search: (prev) => ({ ...prev, preview: undefined }),
        });
      }}
    />
  );
}

export function RunNotFound() {
  return (
    <NotFoundState
      title="Run not found"
      description="This address does not name a task this workspace has. It may have been mistyped, or the task may have been removed."
    />
  );
}
